import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
  Box,
  Typography,
} from '@material-ui/core';
import {
  Notifications,
  NotificationsOff,
  NotificationsActive,
  NotificationsPaused,
  CheckCircle,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { RootState } from '../../redux/store';
import {
  subscribeToChannel,
  unsubscribeFromChannel,
  muteChannel,
  unmuteChannel,
  fetchChannelSubscriptions,
  fetchCommunityPolicy,
  fetchChannelPolicy,
} from '../../redux/actions/notificationSettingsActions';

const useStyles = makeStyles((theme) => ({
  iconButton: {
    color: 'white',
    backgroundColor: 'rgba(255,255,255,0.2)',
    backdropFilter: 'blur(10px)',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.3)',
    },
  },
  menuItem: {
    padding: theme.spacing(1.5, 2),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  activeMenuItem: {
    backgroundColor: theme.palette.action.selected,
  },
  menuIcon: {
    minWidth: 40,
  },
  sectionTitle: {
    padding: theme.spacing(1, 2),
    fontWeight: 600,
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
}));

interface ChannelBellMenuProps {
  communityIdentifier: string;
  channelIdentifier: string;
  iconStyle?: React.CSSProperties;
}

const ChannelBellMenu: React.FC<ChannelBellMenuProps> = ({
  communityIdentifier,
  channelIdentifier,
  iconStyle,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentSubscription, setCurrentSubscription] = useState<any>(null);
  const [channelPolicy, setChannelPolicy] = useState<any>(null);
  const [communityPolicy, setCommunityPolicy] = useState<any>(null);

  const {
    channelSubscriptions,
    channelSubscriptionsLoading,
    subscribeToChannelLoading,
    unsubscribeFromChannelLoading,
    muteChannelLoading,
    unmuteChannelLoading,
  } = useSelector((state: RootState) => state.notificationSettingsReducer);

  const isLoading =
    channelSubscriptionsLoading ||
    subscribeToChannelLoading ||
    unsubscribeFromChannelLoading ||
    muteChannelLoading ||
    unmuteChannelLoading;

  // Fetch subscriptions and policies on mount
  useEffect(() => {
    dispatch(fetchChannelSubscriptions(communityIdentifier) as any);

    // Fetch community policy
    dispatch(fetchCommunityPolicy(communityIdentifier) as any)
      .then((res: any) => {
        if (res?.data) {
          setCommunityPolicy(res.data);
        }
      })
      .catch(() => {
        // Policy might not exist, that's okay
        setCommunityPolicy(null);
      });

    // Fetch channel-specific policy
    dispatch(fetchChannelPolicy(communityIdentifier, channelIdentifier) as any)
      .then((res: any) => {
        if (res?.data) {
          setChannelPolicy(res.data);
        }
      })
      .catch(() => {
        // Policy might not exist, that's okay
        setChannelPolicy(null);
      });
  }, [dispatch, communityIdentifier, channelIdentifier]);

  // Update current subscription when list changes
  useEffect(() => {
    const subscription = channelSubscriptions.find(
      (sub) =>
        sub.communityIdentifier === communityIdentifier &&
        sub.channelIdentifier === channelIdentifier
    );
    setCurrentSubscription(subscription || null);
  }, [channelSubscriptions, communityIdentifier, channelIdentifier]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSubscribe = (frequency: 'immediate' | 'daily' | 'off') => {
    dispatch(
      subscribeToChannel(communityIdentifier, channelIdentifier, {
        frequency,
        emailEnabled: true,
        pushEnabled: false,
      }) as any
    );
    handleClose();
  };

  const handleUnsubscribe = () => {
    dispatch(unsubscribeFromChannel(communityIdentifier, channelIdentifier) as any);
    handleClose();
  };

  const handleMute = () => {
    dispatch(muteChannel(communityIdentifier, channelIdentifier, 24) as any);
    handleClose();
  };

  const handleUnmute = () => {
    dispatch(unmuteChannel(communityIdentifier, channelIdentifier) as any);
    handleClose();
  };

  const isMuted = currentSubscription?.mutedUntil && new Date(currentSubscription.mutedUntil) > new Date();

  // Determine effective frequency with correct precedence
  const getEffectiveFrequency = (): 'immediate' | 'daily' | 'off' => {
    // 1. If channel policy is ENFORCED → always use it (admin override)
    if (channelPolicy?.policyMode === 'enforced') {
      return channelPolicy?.defaults?.frequency || 'daily';
    }

    // 2. If community policy is ENFORCED → always use it (admin override)
    if (communityPolicy?.policyMode === 'enforced') {
      return communityPolicy?.defaults?.globalFrequency || 'daily';
    }

    // 3. If user has a subscription → use user's preference
    if (currentSubscription?.frequency) {
      return currentSubscription.frequency;
    }

    // 4. If channel policy is RECOMMENDED → use as default
    if (channelPolicy?.defaults?.frequency) {
      return channelPolicy.defaults.frequency;
    }

    // 5. If community policy is RECOMMENDED → use as default
    if (communityPolicy?.defaults?.globalFrequency) {
      return communityPolicy.defaults.globalFrequency;
    }

    // 6. Fallback to 'daily'
    return 'daily';
  };

  const currentFrequency = getEffectiveFrequency();

  // Check if settings are enforced by admin
  const isEnforced =
    channelPolicy?.policyMode === 'enforced' ||
    communityPolicy?.policyMode === 'enforced';

  return (
    <>
      <IconButton
        className={classes.iconButton}
        onClick={handleClick}
        disabled={isLoading}
        style={iconStyle}
        title="Notification settings"
      >
        {isLoading ? (
          <CircularProgress size={20} style={{ color: 'white' }} />
        ) : isMuted ? (
          <NotificationsPaused />
        ) : currentFrequency === 'immediate' ? (
          <NotificationsActive />
        ) : currentFrequency === 'daily' ? (
          <Notifications />
        ) : (
          <NotificationsOff />
        )}
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Typography className={classes.sectionTitle}>Channel Notifications</Typography>
        <Divider />

        {!currentSubscription ? (
          <>
            <MenuItem
              className={`${classes.menuItem} ${
                currentFrequency === 'immediate' ? classes.activeMenuItem : ''
              }`}
              onClick={() => handleSubscribe('immediate')}
            >
              <ListItemIcon className={classes.menuIcon}>
                <NotificationsActive fontSize="small" />
                {currentFrequency === 'immediate' && (
                  <CheckCircle
                    fontSize="small"
                    style={{ marginLeft: -8 }}
                    color="primary"
                  />
                )}
              </ListItemIcon>
              <ListItemText
                primary="Immediate"
                secondary="Get notified right away"
              />
            </MenuItem>
            <MenuItem
              className={`${classes.menuItem} ${
                currentFrequency === 'daily' ? classes.activeMenuItem : ''
              }`}
              onClick={() => handleSubscribe('daily')}
            >
              <ListItemIcon className={classes.menuIcon}>
                <Notifications fontSize="small" />
                {currentFrequency === 'daily' && (
                  <CheckCircle
                    fontSize="small"
                    style={{ marginLeft: -8 }}
                    color="primary"
                  />
                )}
              </ListItemIcon>
              <ListItemText
                primary="Daily Digest"
                secondary="Once per day summary"
              />
            </MenuItem>
            <MenuItem
              className={`${classes.menuItem} ${
                currentFrequency === 'off' ? classes.activeMenuItem : ''
              }`}
              onClick={() => handleSubscribe('off')}
            >
              <ListItemIcon className={classes.menuIcon}>
                <NotificationsOff fontSize="small" />
                {currentFrequency === 'off' && (
                  <CheckCircle
                    fontSize="small"
                    style={{ marginLeft: -8 }}
                    color="primary"
                  />
                )}
              </ListItemIcon>
              <ListItemText
                primary="Off"
                secondary="No notifications"
              />
            </MenuItem>
          </>
        ) : (
          <>
            <MenuItem
              className={`${classes.menuItem} ${
                currentFrequency === 'immediate' && !isMuted ? classes.activeMenuItem : ''
              }`}
              onClick={() => handleSubscribe('immediate')}
            >
              <ListItemIcon className={classes.menuIcon}>
                <NotificationsActive fontSize="small" />
                {currentFrequency === 'immediate' && !isMuted && (
                  <CheckCircle
                    fontSize="small"
                    style={{ marginLeft: -8 }}
                    color="primary"
                  />
                )}
              </ListItemIcon>
              <ListItemText
                primary="Immediate"
                secondary="Get notified right away"
              />
            </MenuItem>

            <MenuItem
              className={`${classes.menuItem} ${
                currentFrequency === 'daily' && !isMuted ? classes.activeMenuItem : ''
              }`}
              onClick={() => handleSubscribe('daily')}
            >
              <ListItemIcon className={classes.menuIcon}>
                <Notifications fontSize="small" />
                {currentFrequency === 'daily' && !isMuted && (
                  <CheckCircle
                    fontSize="small"
                    style={{ marginLeft: -8 }}
                    color="primary"
                  />
                )}
              </ListItemIcon>
              <ListItemText
                primary="Daily Digest"
                secondary="Once per day summary"
              />
            </MenuItem>

            <MenuItem
              className={`${classes.menuItem} ${
                currentFrequency === 'off' ? classes.activeMenuItem : ''
              }`}
              onClick={() => handleSubscribe('off')}
            >
              <ListItemIcon className={classes.menuIcon}>
                <NotificationsOff fontSize="small" />
                {currentFrequency === 'off' && (
                  <CheckCircle
                    fontSize="small"
                    style={{ marginLeft: -8 }}
                    color="primary"
                  />
                )}
              </ListItemIcon>
              <ListItemText
                primary="Off"
                secondary="No notifications"
              />
            </MenuItem>

            <Divider />

            {isMuted ? (
              <MenuItem className={classes.menuItem} onClick={handleUnmute}>
                <ListItemIcon className={classes.menuIcon}>
                  <Notifications fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Unmute"
                  secondary={`Muted until ${new Date(currentSubscription.mutedUntil).toLocaleString()}`}
                />
              </MenuItem>
            ) : (
              <MenuItem className={classes.menuItem} onClick={handleMute}>
                <ListItemIcon className={classes.menuIcon}>
                  <NotificationsPaused fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Mute for 24 hours"
                  secondary="Temporarily pause notifications"
                />
              </MenuItem>
            )}

            <Divider />

            <MenuItem className={classes.menuItem} onClick={handleUnsubscribe}>
              <ListItemIcon className={classes.menuIcon}>
                <NotificationsOff fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText
                primary="Unsubscribe"
                secondary="Stop following this channel"
                primaryTypographyProps={{ style: { color: '#d32f2f' } }}
              />
            </MenuItem>
          </>
        )}

        {!currentSubscription && !isEnforced && (
          <Box p={2} maxWidth={280} style={{ backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
            <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
              📋 Using Community Default
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {currentFrequency === 'immediate' && "You'll receive immediate notifications based on community settings. Click an option above to customize."}
              {currentFrequency === 'daily' && "You'll receive daily digest emails based on community settings. Click an option above to customize."}
              {currentFrequency === 'off' && "Notifications are off by default. Click an option above to subscribe to this channel."}
            </Typography>
          </Box>
        )}

        {isEnforced && (
          <Box p={2} maxWidth={280} style={{ backgroundColor: '#fff3e0', borderRadius: '4px' }}>
            <Typography variant="caption" style={{ color: '#e65100', fontWeight: 600 }}>
              ⚠️ Admin Policy Active
            </Typography>
            <Typography variant="caption" style={{ color: '#e65100', display: 'block', marginTop: 4 }}>
              {channelPolicy?.policyMode === 'enforced'
                ? `Channel administrators have enforced ${currentFrequency} notifications for this channel.`
                : `Community administrators have enforced ${currentFrequency} notifications.`}
            </Typography>
          </Box>
        )}
      </Menu>
    </>
  );
};

export default ChannelBellMenu;
